import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Lock, Mail, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { BRAND } from "../data/mock";

export default function AdminLogin() {
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || sessionStorage.getItem("bf_admin_next") || "/admin";
  const sessionMatch = location.hash.match(/session_id=([^&]+)/);
  const [googleBusy, setGoogleBusy] = useState(!!sessionMatch);
  const exchanged = useRef(false);

  useEffect(() => {
    if (!sessionMatch || exchanged.current) return;
    exchanged.current = true;
    googleLogin(sessionMatch[1])
      .then(() => { sessionStorage.removeItem("bf_admin_next"); toast.success("Welcome back!"); navigate(from, { replace: true }); })
      .catch((err) => { toast.error(err?.response?.data?.detail || "Google sign-in failed"); navigate("/admin/login", { replace: true }); setGoogleBusy(false); });
  }, [sessionMatch, googleLogin, navigate, from]);

  const startGoogle = () => {
    sessionStorage.setItem("bf_admin_next", from);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Login failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] grain relative flex items-center justify-center px-4 py-12" data-testid="admin-login-page">
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-[#FC5106]/15 blur-3xl rounded-full" />
      <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-[#3915AA]/15 blur-3xl rounded-full" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-8 justify-center">
          <img src={BRAND.logoWhiteUrl} alt="BarknFurs" data-testid="admin-login-logo" className="h-10 w-auto object-contain" />
          <div className="sr-only">
            <p className="font-heading font-bold text-xl text-white">BarknFurs</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9BCFF]">Admin Console</p>
          </div>
        </Link>

        <div className="rounded-3xl border border-white/10 bg-[#121212] p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-[#FC5106]/15 border border-[#FC5106]/40 w-11 h-11 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#FC5106]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-white">Sign in</h1>
              <p className="text-white/50 text-xs">Manage bookings & enquiries</p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  data-testid="admin-login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@barknfurs.com"
                  className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  data-testid="admin-login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
              ) : (
                <>Sign in <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wider text-white/30"><span className="h-px flex-1 bg-white/10" />or<span className="h-px flex-1 bg-white/10" /></div>
          <button type="button" onClick={startGoogle} disabled={googleBusy} data-testid="admin-login-google"
            className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white py-3 text-sm font-bold text-[#1E0A5C] transition-colors hover:bg-[#F7F5FF] disabled:opacity-60">
            {googleBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" />}
            {googleBusy ? "Signing you in…" : "Continue with Google"}
          </button>
          <p className="mt-2 text-center text-[11px] text-white/40">Only team members added in Team can sign in with Google.</p>

          <p className="text-center text-white/40 text-xs mt-6">
            Authorised personnel only · For password reset, contact site admin.
          </p>
        </div>

        <Link to="/" className="mt-6 block text-center text-white/50 text-xs hover:text-white transition">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
